import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import BlockedSlot from "./BlockedSlot";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

import {
  FiCalendar,
  FiClock,
  FiUser,
  FiMapPin,
  FiVideo,
  FiCheckCircle,
  FiXCircle,
  FiBriefcase,
  FiMail,
  FiPhone,
  FiRefreshCw,
  FiInfo,
  FiAlertCircle,
  FiSlash
} from "react-icons/fi";
import {
  MdOutlineEventNote,
  MdOutlineGavel,
  MdOutlineCancel,
  MdOutlineEventAvailable,
  MdOutlineToday,
  MdOutlineSchedule
} from "react-icons/md";

import { MdOutlineEventBusy } from "react-icons/md";

import "../styles/Calendar.css";

export default function ProfessionalCalendar() {
  const { user, token } = useAuth();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [calendarView, setCalendarView] = useState("dayGridMonth");
  const [consultationAvailability, setConsultationAvailability] = useState({
    status: "NOT_AVAILABLE",
    startTime: "",
    endTime: ""
  });
  const [showBlockedSlot, setShowBlockedSlot] = useState(false);

  useEffect(() => {
    fetchCalendar();
  }, []);

  /* ======================
     FETCH CALENDAR DATA
  ====================== */
  const fetchCalendar = async () => {
    try {
      setLoading(true);

      /* 🔹 APPOINTMENT EVENTS */
      const appointmentRes = await fetch(
        `${API_BASE_URL}/api/appointments/calendar`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!appointmentRes.ok) throw new Error("Failed to fetch appointments");

      const appointmentData = await appointmentRes.json();
      const appointmentEvents = (appointmentData.appointments || []).map(app => ({
        id: `appointment-${app._id}`,
        title: user.role === "SOLICITOR"
          ? `${app.userDetails?.name || "Client"}`
          : `${app.solicitorId?.name || "Solicitor"}`,
        start: app.appointmentDate,
        end: new Date(new Date(app.appointmentDate).getTime() + 30 * 60000),
        classNames: app.appointmentCallStatus === "COMPLETED"
          ? ["calendar-event-completed"]
          : ["calendar-event-pending"],
        extendedProps: {
          type: "APPOINTMENT",
          videoCallLink: app.videoCallLink,
          appointmentCallStatus: app.appointmentCallStatus,
          solicitorName: app.solicitorId?.name,
          clientName: app.userDetails?.name,
          status: app.status,
          practiceArea: app.practiceArea
        }
      }));

      /* 🔹 HEARING EVENTS */
      const hearingRes = await fetch(
        `${API_BASE_URL}/api/cases/hearings/calendar`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!hearingRes.ok) throw new Error("Failed to fetch hearings");

      const hearingData = await hearingRes.json();
      const hearingEvents = (hearingData.hearings || []).map(h => ({
        id: `hearing-${h._id}`,
        title: h.statusAtHearing === "CANCELLED"
          ? `${h.appointmentId || "Hearing"}`
          : `${h.appointmentId || "Court Hearing"}`,
        start: h.hearingDateTime,
        end: new Date(new Date(h.hearingDateTime).getTime() + 60 * 60000),
        classNames: h.statusAtHearing === "CANCELLED"
          ? ["calendar-event-cancelled"]
          : ["calendar-event-hearing"],
        extendedProps: {
          type: "HEARING",
          statusAtHearing: h.statusAtHearing,
          courtName: h.courtDetails?.courtName,
          courtLocation: h.courtDetails?.courtLocation,
          remarks: h.courtDetails?.remarks,
          userName: h.userDetails?.name,
          userEmail: h.userDetails?.email,
          userMobile: h.userDetails?.mobile,
          userCountryCode: h.userDetails?.countryCode,
          caseId: h.caseId
        }
      }));

      setEvents([...appointmentEvents, ...hearingEvents]);
      toast.success("Calendar loaded successfully");
    } catch (err) {
      console.error("Calendar fetch error:", err);
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.consultationAvailability) {
      const { status, startTime, endTime } = user.consultationAvailability;

      setConsultationAvailability({
        status: status || "NOT_AVAILABLE",
        startTime: startTime || "",
        endTime: endTime || ""
      });
    }
  }, [user]);


  const saveConsultationAvailability = async (updated) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/auth/solicitor/consultation-availability`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(updated)
        }
      );

      if (!res.ok) throw new Error("Failed");

      const data = await res.json();

      // ✅ UPDATE LOCAL STATE FROM RESPONSE
      setConsultationAvailability(data.consultationAvailability);

      toast.success("Consultation availability updated");
    } catch {
      toast.error("Failed to update availability");
    }
  };



  /* ======================
     HANDLE EVENT CLICK
  ====================== */
  const handleEventClick = (info) => {
    setSelectedEvent(info.event);
  };

  /* ======================
     HANDLE VIEW CHANGE
  ====================== */
  const handleViewChange = (viewInfo) => {
    setCalendarView(viewInfo.view.type);
  };

  /* ======================
     JOIN MEETING
  ====================== */
  const handleJoinMeeting = () => {
    if (!selectedEvent) return;

    const link = selectedEvent.extendedProps.videoCallLink;
    if (!link) {
      toast.error("Video call link not available");
      return;
    }

    Swal.fire({
      title: "Join Video Call",
      html: `
        <div style="text-align: left; line-height: 1.6;">
          <p><strong>Meeting Type:</strong> Appointment</p>
          <p><strong>Scheduled Time:</strong> ${new Date(selectedEvent.start).toLocaleString()}</p>
          <p><strong>With:</strong> ${selectedEvent.extendedProps.solicitorName || selectedEvent.extendedProps.clientName}</p>
          
          <div style="margin-top: 15px; background: #f0f9ff; padding: 12px; border-radius: 8px; border-left: 4px solid #3b82f6;">
            <p style="margin: 0; color: #1e40af; font-weight: 500;">
              <strong>Note:</strong> Ensure you have a stable internet connection before joining.
            </p>
          </div>
        </div>
      `,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Join Call Now",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#6b7280",
      width: "550px"
    }).then((result) => {
      if (result.isConfirmed) {
        window.open(link, "_blank");
      }
    });
  };

  /* ======================
     CLOSE POPUP
  ====================== */
  const closePopup = () => {
    setSelectedEvent(null);
  };

  /* ======================
     RENDER EVENT CONTENT
  ====================== */
  const renderEventContent = (eventInfo) => {
    const isHearing = eventInfo.event.extendedProps.type === "HEARING";
    const isCompleted = eventInfo.event.extendedProps.appointmentCallStatus === "COMPLETED";
    const isCancelled = eventInfo.event.extendedProps.statusAtHearing === "CANCELLED";

    return (
      <div className="calendar-event-content">
        {isHearing ? (
          <MdOutlineGavel size={12} className="calendar-event-icon" />
        ) : (
          <FiVideo size={12} className="calendar-event-icon" />
        )}
        <div className="calendar-event-title">
          {eventInfo.event.title}
        </div>
        <div className="calendar-event-time">
          {eventInfo.timeText}
        </div>
      </div>
    );
  };

  /* ======================
     RENDER STATS
  ====================== */
  const renderCalendarStats = () => {
    const appointments = events.filter(e => e.extendedProps.type === "APPOINTMENT");
    const hearings = events.filter(e => e.extendedProps.type === "HEARING");

    const pendingAppointments = appointments.filter(a =>
      a.extendedProps.appointmentCallStatus !== "COMPLETED"
    ).length;

    const completedAppointments = appointments.filter(a =>
      a.extendedProps.appointmentCallStatus === "COMPLETED"
    ).length;

    const cancelledHearings = hearings.filter(h =>
      h.extendedProps.statusAtHearing === "CANCELLED"
    ).length;

    return (
      <div className="calendar-stats-grid">
        <div className="calendar-stat-card calendar-stat-appointments">
          <div className="calendar-stat-icon">
            <MdOutlineSchedule size={24} />
          </div>
          <div className="calendar-stat-content">
            <h3 className="calendar-stat-value">{appointments.length}</h3>
            <p className="calendar-stat-label">Total Appointments</p>
            <span className="calendar-stat-subtext">
              {pendingAppointments} pending, {completedAppointments} completed
            </span>
          </div>
        </div>

        <div className="calendar-stat-card calendar-stat-hearings">
          <div className="calendar-stat-icon">
            <MdOutlineGavel size={24} />
          </div>
          <div className="calendar-stat-content">
            <h3 className="calendar-stat-value">{hearings.length}</h3>
            <p className="calendar-stat-label">Court Hearings</p>
            <span className="calendar-stat-subtext">
              {cancelledHearings} cancelled
            </span>
          </div>
        </div>

        <div className="calendar-stat-card calendar-stat-upcoming">
          <div className="calendar-stat-icon">
            <MdOutlineToday size={24} />
          </div>
          <div className="calendar-stat-content">
            <h3 className="calendar-stat-value">
              {events.filter(e =>
                new Date(e.start) >= new Date() &&
                new Date(e.start) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
              ).length}
            </h3>
            <p className="calendar-stat-label">Upcoming (7 Days)</p>
            <span className="calendar-stat-subtext">
              Next scheduled events
            </span>
          </div>
        </div>
      </div>
    );
  };

  /* ======================
     RENDER EVENT DETAILS
  ====================== */
  const renderEventDetails = () => {
    if (!selectedEvent) return null;

    const props = selectedEvent.extendedProps;
    const isAppointment = props.type === "APPOINTMENT";

    return (
      <div className="calendar-modal-overlay">
        <div className="calendar-modal">
          <div className="calendar-modal-header">
            <div className="calendar-modal-title">
              {isAppointment ? <FiVideo size={20} /> : <MdOutlineGavel size={20} />}
              <h3>{isAppointment ? "Appointment Details" : "Court Hearing Details"}</h3>
            </div>
            <button className="calendar-modal-close" onClick={closePopup}>
              &times;
            </button>
          </div>

          <div className="calendar-modal-body">
            {/* Event Type Badge */}
            <div className={`calendar-event-type-badge ${isAppointment ? 'appointment' : 'hearing'}`}>
              {isAppointment ? "APPOINTMENT" : "COURT HEARING"}
            </div>

            {/* Date & Time */}
            <div className="calendar-details-section">
              <h4>
                <FiCalendar size={16} />
                <span>Date & Time</span>
              </h4>
              <div className="calendar-details-grid">
                <div className="calendar-detail-item">
                  <strong><FiCalendar size={14} /> Date:</strong>
                  <span>{new Date(selectedEvent.start).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
                <div className="calendar-detail-item">
                  <strong><FiClock size={14} /> Time:</strong>
                  <span>{new Date(selectedEvent.start).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                </div>
              </div>
            </div>

            {/* Appointment Details */}
            {isAppointment ? (
              <>
                <div className="calendar-details-section">
                  <h4>
                    <FiBriefcase size={16} />
                    <span>Appointment Information</span>
                  </h4>
                  <div className="calendar-details-grid">
                    <div className="calendar-detail-item">
                      <strong><FiUser size={14} /> With:</strong>
                      <span>
                        {user.role === "SOLICITOR"
                          ? props.clientName || "Client"
                          : props.solicitorName || "Solicitor"
                        }
                      </span>
                    </div>
                    <div className="calendar-detail-item">
                      <strong>Status:</strong>
                      <span className={`calendar-status-badge ${props.appointmentCallStatus?.toLowerCase()}`}>
                        {props.appointmentCallStatus === "COMPLETED" ? (
                          <>
                            <FiCheckCircle size={12} />
                            <span>Completed</span>
                          </>
                        ) : (
                          <>
                            <MdOutlineSchedule size={12} />
                            <span>Scheduled</span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {props.practiceArea && (
                  <div className="calendar-details-section">
                    <h4>
                      <FiInfo size={16} />
                      <span>Case Information</span>
                    </h4>
                    <div className="calendar-detail-item">
                      <strong>Practice Area:</strong>
                      <span>{props.practiceArea}</span>
                    </div>
                  </div>
                )}

                {/* Join Meeting Button */}
                {props.appointmentCallStatus === "NOT_STARTED" && (
                  <div className="calendar-actions">
                    <button
                      className="calendar-join-meeting-btn"
                      onClick={handleJoinMeeting}
                    >
                      <FiVideo size={16} />
                      <span>Join Video Meeting</span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              /* Hearing Details */
              <>
                <div className="calendar-details-section">
                  <h4>
                    <FiMapPin size={16} />
                    <span>Court Information</span>
                  </h4>
                  <div className="calendar-details-grid">
                    <div className="calendar-detail-item">
                      <strong>Court Name:</strong>
                      <span>{props.courtName || "Not specified"}</span>
                    </div>
                    <div className="calendar-detail-item">
                      <strong>Location:</strong>
                      <span>{props.courtLocation || "Not specified"}</span>
                    </div>
                  </div>
                </div>

                {/* Hearing Status */}
                <div className="calendar-details-section">
                  <h4>
                    <FiAlertCircle size={16} />
                    <span>Hearing Status</span>
                  </h4>
                  <div className={`calendar-hearing-status ${props.statusAtHearing?.toLowerCase()}`}>
                    {props.statusAtHearing === "CANCELLED" ? (
                      <>
                        <FiXCircle size={16} />
                        <div>
                          <strong>Cancelled Hearing</strong>
                          <p>This hearing has been cancelled</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <MdOutlineGavel size={16} />
                        <div>
                          <strong>Scheduled Hearing</strong>
                          <p>Court hearing is scheduled</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Client Details (for solicitors) */}
                {user.role === "SOLICITOR" && (
                  <div className="calendar-details-section">
                    <h4>
                      <FiUser size={16} />
                      <span>Client Details</span>
                    </h4>
                    <div className="calendar-details-grid">
                      <div className="calendar-detail-item">
                        <strong><FiUser size={14} /> Name:</strong>
                        <span>{props.userName || "Not specified"}</span>
                      </div>
                      <div className="calendar-detail-item">
                        <strong><FiMail size={14} /> Email:</strong>
                        <span>{props.userEmail || "Not specified"}</span>
                      </div>
                      <div className="calendar-detail-item">
                        <strong><FiPhone size={14} /> Mobile:</strong>
                        <span>
                          {props.userCountryCode || ""} {props.userMobile || "Not specified"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Remarks */}
            {props.remarks && (
              <div className="calendar-details-section">
                <h4>
                  <FiInfo size={16} />
                  <span>Remarks</span>
                </h4>
                <div className="calendar-remarks">
                  {props.remarks}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="premium-calendar-container">
      {/* ======================
         HEADER
      ====================== */}
      <div className="calendar-header">
        <div className="calendar-header-left">
          <h1 className="calendar-page-title">Appointments & Hearings</h1>
          <p className="calendar-page-subtitle">
            View and manage your scheduled appointments and court hearings
          </p>
        </div>
        <div className="calendar-header-right">

          {user?.role === "SOLICITOR" && (
            <div className="calendar-consultation-controls">

              {/* Toggle */}
              <label className="calendar-toggle">
                <input
                  type="checkbox"
                  checked={consultationAvailability.status === "AVAILABLE"}
                  onChange={(e) => {
                    const isAvailable = e.target.checked;

                    if (
                      isAvailable &&
                      (!consultationAvailability.startTime ||
                        !consultationAvailability.endTime)
                    ) {
                      toast.error("Please select start and end time");
                      return;
                    }

                    const updated = {
                      ...consultationAvailability,
                      status: isAvailable ? "AVAILABLE" : "NOT_AVAILABLE"
                    };

                    setConsultationAvailability(updated);
                    saveConsultationAvailability(updated);
                  }}
                />
                <span className="calendar-toggle-slider"></span>
              </label>

              {/* Time slot */}
              <div className="calendar-time-range">
                <input
                  type="time"
                  value={consultationAvailability.startTime}
                  onChange={(e) => {
                    const updated = {
                      ...consultationAvailability,
                      startTime: e.target.value
                    };

                    setConsultationAvailability(updated);

                    if (updated.startTime && updated.endTime) {
                      saveConsultationAvailability(updated);
                    }
                  }}
                />

                <span>to</span>

                <input
                  type="time"
                  value={consultationAvailability.endTime}
                  onChange={(e) => {
                    const updated = {
                      ...consultationAvailability,
                      endTime: e.target.value
                    };

                    setConsultationAvailability(updated);

                    if (updated.startTime && updated.endTime) {
                      saveConsultationAvailability(updated);
                    }
                  }}
                />
              </div>

              {/* ✅ NEW : Block your slots button */}
              <button
                type="button"
                className="calendar-refresh-btn"
                onClick={() => setShowBlockedSlot(true)}
              >
                <MdOutlineEventBusy size={18} />
                Unavailability
              </button>

            </div>
          )}


          <button
            className="calendar-refresh-btn"
            onClick={fetchCalendar}
            disabled={loading}
          >
            <FiRefreshCw size={18} className={loading ? "spinning" : ""} />
            <span>{loading ? "Refreshing..." : "Refresh"}</span>
          </button>

        </div>




      </div>

      {/* ======================
         STATS
      ====================== */}
      {renderCalendarStats()}

      {/* ======================
         CALENDAR CONTENT
      ====================== */}
      <div className="calendar-content">
        <div className="calendar-wrapper">
          {loading ? (
            <div className="calendar-loading-state">
              <div className="calendar-loading-spinner"></div>
              <p>Loading calendar...</p>
            </div>
          ) : (
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={calendarView}
              showNonCurrentDates={false}
              fixedWeekCount={false}
              headerToolbar={{
                left: "prev,next",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay"
              }}
              events={events}
              eventClick={handleEventClick}
              eventContent={renderEventContent}
              datesSet={handleViewChange}
              height="auto"
              slotMinTime="08:00:00"
              slotMaxTime="22:00:00"
              slotDuration="00:30:00"
              slotLabelInterval="01:00:00"
              allDaySlot={false}
              dayMaxEvents={3}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                meridiem: false
              }}
            />
          )}
        </div>
      </div>

      {/* ======================
         EVENT DETAILS POPUP
      ====================== */}
      {renderEventDetails()}
      {showBlockedSlot && (
        <BlockedSlot onClose={() => setShowBlockedSlot(false)} />
      )}
    </div>
  );
}